import { useEffect, useRef } from "react"
import EmailVerifyComponent from "@/auth/components/email-verify"

import { sendAnalyticsEvent } from "@/lib/analytics/amplitude-utils"
import { isEmbedded } from "@/lib/is-embedded"
import { useFeatureFlag } from "@/lib/storage/api/feature-flag-api"
import {
  selectAuthFlowComponent,
  selectAuthFlowStep,
} from "@/lib/storage/auth-slice"
import { useAppSelector } from "@/lib/storage/hooks"
import { AuthFlowHeaders } from "@/components/auth/auth-flow-headers"
import AuthStepEmail from "@/components/auth/auth-step-email"
import AuthStepRegister from "@/components/auth/auth-step-register"
import AuthStepSignIn from "@/components/auth/auth-step-sign-in"
import { PhoneSignIn } from "@/components/auth/phone-sign-in"
import ResetPassword from "@/components/auth/reset-password"
import ForgottenPasswordComponent from "./forgotten-password"

export default function AuthFlow({
  phoneAuthOptIn = false,
  surface,
}: {
  phoneAuthOptIn?: boolean
  surface?: "page" | "dialog"
}) {
  const phoneAuthFlagEnabled = useFeatureFlag("phone_auth")
  const phoneAuthEnabled =
    (phoneAuthFlagEnabled || phoneAuthOptIn) && !isEmbedded()
  const component = useAppSelector(selectAuthFlowComponent)
  const step = useAppSelector(selectAuthFlowStep)

  const phoneFlowVisible =
    phoneAuthEnabled && component === "auth-form" && step === "email"
  const emailStep = component === "auth-form" ? step : component
  const lastViewedStep = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (phoneFlowVisible) {
      lastViewedStep.current = undefined
    } else if (lastViewedStep.current !== emailStep) {
      lastViewedStep.current = emailStep
      sendAnalyticsEvent({
        name: "AuthStepView",
        properties: {
          method: "email",
          step: emailStep,
          ...(surface && { surface }),
        },
      })
    }
  }, [phoneFlowVisible, emailStep, surface])

  function renderStep() {
    switch (component) {
      case "auth-form":
        switch (step) {
          case "email":
            if (phoneAuthEnabled) {
              return (
                <PhoneSignIn
                  enablePhoneSignup={phoneAuthOptIn}
                  surface={surface}
                />
              )
            }
            return <AuthStepEmail />
          case "signin":
            return <AuthStepSignIn />
          case "register":
            return <AuthStepRegister />
        }
        break
      case "forgotten":
        return <ForgottenPasswordComponent />
      case "verify_email":
        return <EmailVerifyComponent />
      case "reset_password":
        return <ResetPassword />
    }
  }

  return (
    // min-h-108 matches the first step's height, so a shorter step leaves
    // space at the bottom instead of moving the header in the centered page
    // or dialog.
    <div className="mx-auto flex min-h-108 w-full flex-col gap-4 leading-tight sm:max-w-108">
      {/* The phone flow renders its own, since its steps live in its state. */}
      {!phoneFlowVisible && <AuthFlowHeaders />}
      {renderStep()}
    </div>
  )
}
